/**
 * Consumer Trust Wallet · sign-in.
 *
 * Route: /wallet/login
 *
 * Presents the consumer with the choice of identity provider — Google,
 * Apple, Microsoft, Email, Magic Link, Passkey. The wallet never forces
 * a Microsoft account on a consumer. All providers currently run in dev
 * / sandbox mode; live wiring flips per adapter when credentials are
 * available.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Chrome, Apple, Mail, Fingerprint, Wand2, ArrowRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const PROVIDER_META = {
  google:              { icon: Chrome,      label: "Continue with Google" },
  apple:               { icon: Apple,       label: "Continue with Apple" },
  microsoft_consumer:  { icon: ArrowRight,  label: "Continue with Microsoft" },
  email:               { icon: Mail,        label: "Continue with Email" },
  magic_link:          { icon: Wand2,       label: "Passwordless Magic Link" },
  passkey:             { icon: Fingerprint, label: "Sign in with Passkey" },
};

export default function ConsumerLoginPage() {
  const [providers, setProviders] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    axios.get(`${BACKEND}/api/identity/providers?kind=consumer`)
      .then((r) => setProviders(r.data))
      .catch(() => toast.error("Could not load login providers"));
  }, []);

  async function signIn(providerId, credential) {
    setBusy(providerId);
    try {
      const { data } = await axios.post(`${BACKEND}/api/identity/auth/consumer`, {
        provider_id: providerId, credential,
      });
      toast.success(`Signed in as ${data.display_name}`);
      // Store the altara_id locally so the wallet can hydrate.
      window.localStorage.setItem("altara_identity_id", data.altara_id);
      nav(`/wallet`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-in failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="consumer-login">
      <div className="max-w-md w-full space-y-6">
        <header className="text-center space-y-2">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
            Consumer Trust Wallet
          </div>
          <h1 className="font-display text-3xl font-semibold" style={{ color: NAVY }}>
            Welcome to your Trust Wallet
          </h1>
          <p className="text-[13px] text-slate-600">
            Sign in with the provider you prefer. Altara never sees your password — providers verify who you are, Altara manages your digital trust.
          </p>
        </header>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 space-y-2">
          {providers.filter((p) => p.provider_id !== "email").map((p) => {
            const meta = PROVIDER_META[p.provider_id] || {};
            const Icon = meta.icon || ArrowRight;
            return (
              <button
                key={p.provider_id}
                data-testid={`login-${p.provider_id}`}
                disabled={!!busy}
                onClick={() => signIn(p.provider_id, { subject: `demo-${p.provider_id}` })}
                className="w-full flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1 text-left">{meta.label || p.display_name}</span>
                <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
                  style={{ background: p.is_live ? "#D1FAE5" : "#FEF3C7",
                           color:      p.is_live ? "#065F46" : "#92400E" }}>
                  {p.is_live ? "Live" : "Sandbox"}
                </span>
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 space-y-3">
          <label className="block text-[11px] uppercase tracking-widest font-mono text-slate-500">
            Or with your email
          </label>
          <input
            data-testid="consumer-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
          />
          <button
            data-testid="login-email"
            disabled={!email || !!busy}
            onClick={() => signIn("email", { email, display_name: email.split("@")[0] })}
            className="w-full rounded-xl px-3 py-2.5 text-[13px] font-semibold text-white disabled:opacity-50"
            style={{ background: NAVY }}
          >
            Continue with Email
          </button>
        </div>

        <p className="text-center text-[10px] text-slate-400">
          By continuing you agree to Altara&apos;s terms. Login providers only prove who you are; Altara Identity owns your Trust Wallet.
        </p>
      </div>
    </div>
  );
}
